import { AccountSnippet } from "../../../api/services";
import { Box, fade, styled, Typography, useTheme } from "@material-ui/core";
import { CaseIcon, PropertyIcon } from "../../../icons";
import { InlineType } from "../../InlineType";
import { AccountStatusChip } from "../../AccountStatus";
import { formatAddress } from "../../../utils/formatAddress";
import React, { useCallback, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { PRIMARY_BLUE } from "../../../theme";

const StyledBox = styled(Box)(({ theme }) => ({
  "&:focus": {
    backgroundColor: fade(PRIMARY_BLUE["700"], 0.08),
    outline: "none",
  },
  "&:hover": {
    backgroundColor: fade(PRIMARY_BLUE["700"], 0.03),
  },
  cursor: "pointer",
}));

export interface AccountSnippetRowProps {
  autofocus?: boolean;
  handleSelect?: Function;
}

export const AccountSnippetRow: React.FC<
  AccountSnippet & AccountSnippetRowProps
> = ({ status, number, properties, autofocus, handleSelect, ...props }) => {
  const theme = useTheme();
  const isGrey = status === "Cancelled" || status === "Closed";

  const rowRef = useRef<HTMLDivElement>(null);
  /* istanbul ignore next: the effort to mock these browser APIs isn't worth it */
  const focus = useCallback(() => {
    if (rowRef.current) {
      rowRef.current.focus();
    }
  }, [rowRef]);

  /* istanbul ignore next */
  useEffect(() => {
    if (autofocus) {
      focus();
    }
  }, [focus, autofocus]);

  const history = useHistory();
  const onSelect = useCallback(
    (e: React.KeyboardEvent | React.MouseEvent) => {
      if (e.type === "keydown") {
        if ((e as React.KeyboardEvent).which !== 13) return; //Only act on enter key
        e.preventDefault();
      }
      if (handleSelect) handleSelect();
      history.push(`/accounts/${number}`);
    },
    [history, number, handleSelect],
  );

  return (
    <StyledBox
      data-testid="AccountSnippetRow"
      p={2}
      color={isGrey ? "text.secondary" : "text.primary"}
      {...{ ref: rowRef }}
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={onSelect}
      {...props}
    >
      <Box display="flex" alignItems="center">
        <Box mr={1} clone>
          <CaseIcon
            height={16}
            width={16}
            fill={
              isGrey
                ? theme?.palette.text.secondary
                : theme?.palette.text.primary
            }
          />
        </Box>
        <InlineType
          textDecoration={status === "Cancelled" ? "line-through" : undefined}
        >
          <Typography variant="body1">{number}</Typography>
        </InlineType>
        <Box ml={1}>
          <AccountStatusChip status={status} />
        </Box>
      </Box>
      {properties.length > 3 || properties.length === 0 ? (
        <Typography>{`${properties.length} Properties`}</Typography>
      ) : (
        properties.map((property) => (
          <Box
            display="flex"
            alignItems="center"
            mt={0.5}
            key={property.id}
            data-testid="AccountSnippetRow__Property"
          >
            <Box mr={1} clone>
              <PropertyIcon
                height={16}
                width={16}
                fill={
                  isGrey
                    ? theme?.palette.text.secondary
                    : theme?.palette.text.primary
                }
              />
            </Box>
            <Typography variant="body2">
              {formatAddress(property.address)}
            </Typography>
          </Box>
        ))
      )}
    </StyledBox>
  );
};
